import { useCallback, useEffect, useState } from "react"
import Chart from 'chart.js/auto'
import vi_VN from 'antd/es/locale/vi_VN'
import dayjs from 'dayjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useLoading } from "../../context/LoadingContext"
import { LineElement, CategoryScale, LinearScale, PointElement } from 'chart.js'
import { Button, ConfigProvider, DatePicker, Form, InputNumber, Modal, Select, Space, Tabs, TreeSelect } from "antd"
import { ArrowDownOutlined, ArrowUpOutlined, DollarOutlined, EyeOutlined, SearchOutlined, TransactionOutlined, UndoOutlined } from '@ant-design/icons'
import { aggregateArrays, getInfoChartRatio, getInfoChartComparision, getInfoChartDetail, getInfoPackages, networkOptions, renderType } from "../../utils/dashboard-utils"
import { ChartRatio, ChartComparison, ChartDetail, ChartComparisonDay, TableDetailPackageByDay, TableDetailPackageByMonth } from '../../components'
import { apiGetListPartner, apiGetMonthlyStats } from "../../api"
import { formatCurrency, formatNumber } from "../../utils/string-utils"
import { mapCompanyAccounts } from "../../assets/constants/constants"
Chart.register(LineElement, CategoryScale, LinearScale, PointElement, ChartDataLabels)

export default function TransactionReports() {
  const [partnerOptions, setPartnerOptions] = useState<any>([])
  const [chartRatio, setChartRatio] = useState<any>({})
  const [chartComparision, setChartComparision] = useState<any>([])
  const [chartDetail, setChartDetail] = useState<any>({})
  const [packages, setPackages] = useState<any>([])
  const [packagesDay, setPackagesDay] = useState<any>([])
  const [vtvcab, setVTVCab] = useState<any>([])
  const [vtvcabDay, setVTVCabDay] = useState<any>([])
  const [total, setTotal] = useState<any>({
    amount: {
      current: 0,
      previous: 0
    },
    revenue: {
      current: 0,
      previous: 0
    },
    profit: {
      current: 0,
      previous: 0
    }
  })
  const [modalDetailPackage, setModalDetailPackage] = useState<any>({ isOpen: false, data: null, packageName: '' })
  const [search, setSearch] = useState<any>({})
  const [formSearch] = Form.useForm()
  const { setIsLoading } = useLoading()

  const initSearch = () => {
    const date = dayjs()
    setSearch({
      currentYear: date?.year(),
      currentMonth: date?.month() + 1,
      previousYear: date.subtract(1, "month").year(),
      previousMonth: date.subtract(1, "month").month() + 1,
      day: date.subtract(1, "day").date(),
    })
  }

  useEffect(() => {
    initSearch()
  }, [])

  const fetchPartners = useCallback(async () => {
    try {
      const { data } = await apiGetListPartner()
      data.partners = data.partners.filter((item: any) => !mapCompanyAccounts[item.cpCode])
      setPartnerOptions(data.partners?.map((item: any) => ({ label: item.name, value: item.cpCode })) || [])
    } catch (error) {
      console.log(error)
    }
  }, [])

  const fetchChartData = useCallback(async () => {
    if (!search.currentYear || !search.currentMonth || !search.previousYear || !search.previousMonth) return
    try {
      setIsLoading(true)
      const { data: current } = await apiGetMonthlyStats({
        year: search.currentYear,
        month: search.currentMonth,
      })
      const { data: previous } = await apiGetMonthlyStats({
        year: search.previousYear,
        month: search.previousMonth,
      })
      let currentData = current?.statistics.filter((item: any) => item.dataType !== 'vtvcab_promotion')
      let previousData = previous?.statistics.filter((item: any) => item.dataType !== 'vtvcab_promotion')
      let vtvcabCurrentData = current?.statistics.filter((item: any) =>
        item.dataType === 'vtvcab' ||
        item.dataType === 'tv360' ||
        item.dataType === 'vtvcab_promotion')
      let vtvcabPreviousData = previous?.statistics.filter((item: any) =>
        item.dataType === 'vtvcab' ||
        item.dataType === 'tv360' ||
        item.dataType === 'vtvcab_promotion')
      let currentDay = [] as any, previousDay = [] as any
      let vtvcabCurrentDay = [] as any, vtvcabPreviousDay = [] as any
      if (search.cpCode) {
        currentData = currentData?.filter((item: any) => item.cpCode === search.cpCode)
        previousData = previousData?.filter((item: any) => item.cpCode === search.cpCode)
        vtvcabCurrentData = vtvcabCurrentData?.filter((item: any) => item.cpCode === search.cpCode)
        vtvcabPreviousData = vtvcabPreviousData?.filter((item: any) => item.cpCode === search.cpCode)
      }
      if (search.network) {
        switch (search.network) {
          case 'datasponsor':
            currentData = currentData?.filter((item: any) => item.network === 'data_sponsor' || item.network === 'datasponsor_zynet')
            previousData = previousData?.filter((item: any) => item.network === 'data_sponsor' || item.network === 'datasponsor_zynet')
            vtvcabCurrentData = vtvcabCurrentData?.filter((item: any) => item.network === 'data_sponsor' || item.network === 'datasponsor_zynet')
            vtvcabPreviousData = vtvcabPreviousData?.filter((item: any) => item.network === 'data_sponsor' || item.network === 'datasponsor_zynet')
            break;
          case 'affapi':
            currentData = currentData?.filter((item: any) => item.network === 'aff_zynet' || item.network === 'aff_sann')
            previousData = previousData?.filter((item: any) => item.network === 'aff_zynet' || item.network === 'aff_sann')
            vtvcabCurrentData = vtvcabCurrentData?.filter((item: any) => item.network === 'aff_zynet' || item.network === 'aff_sann')
            vtvcabPreviousData = vtvcabPreviousData?.filter((item: any) => item.network === 'aff_zynet' || item.network === 'aff_sann')
            break;
          case 'vds':
            currentData = currentData?.filter((item: any) => item.network === 'vds-data-sponsor' || item.network === 'vds-ds-postpaid')
            previousData = previousData?.filter((item: any) => item.network === 'vds-data-sponsor' || item.network === 'vds-ds-postpaid')
            vtvcabCurrentData = vtvcabCurrentData?.filter((item: any) => item.network === 'vds-data-sponsor' || item.network === 'vds-ds-postpaid')
            vtvcabPreviousData = vtvcabPreviousData?.filter((item: any) => item.network === 'vds-data-sponsor' || item.network === 'vds-ds-postpaid')
            break;
          default:
            currentData = currentData?.filter((item: any) => item.network === search.network)
            previousData = previousData?.filter((item: any) => item.network === search.network)
            vtvcabCurrentData = vtvcabCurrentData?.filter((item: any) => item.network === search.network)
            vtvcabPreviousData = vtvcabPreviousData?.filter((item: any) => item.network === search.network)
            break;
        }
      }
      if (search.day > 0) {
        currentData = currentData?.filter((item: any) => {
          if (item.day === search.day) {
            currentDay.push(item)
          }
          return item.day <= search.day
        })
        previousData = previousData?.filter((item: any) => {
          if (item.day === search.day) {
            previousDay.push(item)
          }
          return item.day <= search.day
        })
        vtvcabCurrentData = vtvcabCurrentData?.filter((item: any) => {
          if (item.day === search.day) {
            vtvcabCurrentDay.push(item)
          }
          return item.day <= search.day
        })
        vtvcabPreviousData = vtvcabPreviousData?.filter((item: any) => {
          if (item.day === search.day) {
            vtvcabPreviousDay.push(item)
          }
          return item.day <= search.day
        })
      }
      const ratio = getInfoChartRatio(search.day > 0 ? currentDay : currentData)
      const comparision = getInfoChartComparision(search.day > 0 ? currentDay : currentData, search.day > 0 ? previousDay : previousData)
      const detail = getInfoChartDetail(search.day > 0 ? currentDay : currentData)
      const revenueCurrent = getInfoPackages(currentData)
      const revenuePrevious = getInfoPackages(previousData)
      const revenueCurrentDay = getInfoPackages(currentDay)
      const revenuePreviousDay = getInfoPackages(previousDay)
      setChartRatio(ratio)
      setChartComparision(comparision)
      setChartDetail(detail)
      setPackages(aggregateArrays(revenueCurrent, revenuePrevious))
      setPackagesDay(aggregateArrays(revenueCurrentDay, revenuePreviousDay))
      const currentRevenue = search.day > 0 ? revenueCurrentDay : revenueCurrent
      const previousRevenue = search.day > 0 ? revenuePreviousDay : revenuePrevious
      setTotal({
        amount: {
          current: currentRevenue.map((item: any) => Number(item.totalAmount || 0)).reduce((a: any, b: any) => a + b, 0),
          previous: previousRevenue.map((item: any) => Number(item.totalAmount || 0)).reduce((a: any, b: any) => a + b, 0),
        },
        revenue: {
          current: currentRevenue.map((item: any) => Number(item.count || 0)).reduce((a: any, b: any) => a + b, 0),
          previous: previousRevenue.map((item: any) => Number(item.count || 0)).reduce((a: any, b: any) => a + b, 0),
        },
        profit: {
          current: currentRevenue.map((item: any) => Number(item.totalProfit || 0)).reduce((a: any, b: any) => a + b, 0),
          previous: previousRevenue.map((item: any) => Number(item.totalProfit || 0)).reduce((a: any, b: any) => a + b, 0),
        },
      })
      const revenueVTVCabCurrent = getInfoPackages(vtvcabCurrentData)
      const revenueVTVCabPrevious = getInfoPackages(vtvcabPreviousData)
      const revenueVTVCabCurrentDay = getInfoPackages(vtvcabCurrentDay)
      const revenueVTVCabPreviousDay = getInfoPackages(vtvcabPreviousDay)
      setVTVCab(aggregateArrays(revenueVTVCabCurrent, revenueVTVCabPrevious))
      setVTVCabDay(aggregateArrays(revenueVTVCabCurrentDay, revenueVTVCabPreviousDay))
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }, [setIsLoading, search])

  useEffect(() => {
    fetchPartners()
    fetchChartData()
  }, [fetchPartners, fetchChartData])

  const handleSearch = (values: any) => {
    const date = dayjs(values.date)
    setSearch({
      cpCode: values.cpCode || '',
      network: values.network || '',
      currentYear: date?.year(),
      currentMonth: date?.month() + 1,
      previousYear: date.subtract(1, "month").year(),
      previousMonth: date.subtract(1, "month").month() + 1,
      day: values.day || 0,
    })
  }

  const handleClearSearch = () => {
    formSearch.resetFields()
    initSearch()
  }

  const renderButtonDetailPackage = (data: any) => (
    <Button
      onClick={() => setModalDetailPackage({ isOpen: true, data: data.detail, packageName: `${renderType(data.dataType)} - ${data.packageName}` })}
      icon={<EyeOutlined />}
    >
    </Button>
  )

  return (
    <div>
      <Form
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'start',
          alignItems: 'center',
          marginBottom: 20,
        }}
        form={formSearch}
        onFinish={handleSearch}
        id="searchForm" layout="vertical" autoComplete="off">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          flexWrap: 'wrap',
          gap: 20,
          width: '100%',
        }}>
          <Form.Item name="cpCode" label={<b>Đối tác</b>}>
            <Select
              style={{ minWidth: 170 }}
              placeholder="Chọn đối tác"
              defaultValue={''}
              options={[
                { label: 'Tất cả', value: '' },
                ...partnerOptions,
              ]}
            />
          </Form.Item>
          <Form.Item name="network" label='Tài khoản'>
            <TreeSelect
              style={{ minWidth: 170 }}
              placeholder="Chọn tài khoản"
              defaultValue={''}
              treeDefaultExpandAll
              treeData={[
                { label: 'Tất cả', value: '' },
                ...networkOptions,
              ]}
            />
          </Form.Item>
          <ConfigProvider locale={vi_VN}>
            <Form.Item name="date" label="Tháng">
              <DatePicker
                defaultValue={dayjs()}
                format="YYYY-MM"
                picker="month"
              />
            </Form.Item>
          </ConfigProvider>
          <Form.Item name="day" label="Ngày" initialValue={dayjs().subtract(1, "day").date()}>
            <InputNumber min={1} max={31} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
            <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
          </Space>
        </div>
      </Form>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 40, marginBottom: 40 }}>
        <div style={{
          textAlign: 'center',
          fontSize: 16,
          fontWeight: 700,
          border: '2px dashed red',
          padding: '12px 20px',
          borderRadius: 10,
        }}>
          <DollarOutlined /> Đã bán: <span style={{
            fontSize: 20,
            color: 'red',
          }}>{formatNumber(total.amount?.current)} gói {total.amount?.current >= total.amount?.previous
            ? <span style={{ color: 'green' }}><ArrowUpOutlined /></span>
            : <span style={{ color: 'red' }}><ArrowDownOutlined /></span>
            }</span>
        </div>
        <div style={{
          textAlign: 'center',
          fontSize: 16,
          fontWeight: 700,
          border: '2px dashed red',
          padding: '12px 20px',
          borderRadius: 10,
        }}>
          <TransactionOutlined /> Doanh thu: <span style={{
            fontSize: 20,
            color: 'red',
          }}>{formatCurrency(total.revenue?.current)} {total.revenue?.current >= total.revenue?.previous
            ? <span style={{ color: 'green' }}><ArrowUpOutlined /></span>
            : <span style={{ color: 'red' }}><ArrowDownOutlined /></span>
            }</span>
        </div>
        {/*Hidden is this version*/}
        {/*<div style={{*/}
        {/*  textAlign: 'center',*/}
        {/*  fontSize: 16,*/}
        {/*  fontWeight: 700,*/}
        {/*  border: '2px dashed red',*/}
        {/*  padding: '12px 20px',*/}
        {/*  borderRadius: 10,*/}
        {/*}}>*/}
        {/*  <DollarOutlined /> NET: <span style={{*/}
        {/*    fontSize: 20,*/}
        {/*    color: 'red',*/}
        {/*  }}>{formatCurrency(total.profit?.current)} {total.profit?.current >= total.profit?.previous*/}
        {/*    ? <span style={{ color: 'green' }}><ArrowUpOutlined /></span>*/}
        {/*    : <span style={{ color: 'red' }}><ArrowDownOutlined /></span>*/}
        {/*    }</span>*/}
        {/*</div>*/}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        {Object.keys(chartRatio)?.length > 0 && (
          <div style={{ flex: 2, maxWidth: '25%', minWidth: 260 }}>
            <ChartRatio data={chartRatio} />
          </div>
        )}
        {chartComparision.length > 0 && (
          <div style={{ flex: 5, maxWidth: '50%', minWidth: 400 }}>
            <ChartComparison data={chartComparision} />
          </div>
        )}
        {Object.keys(chartDetail)?.length > 0 && (
          <div style={{ flex: 2, maxWidth: '25%', minWidth: 260 }}>
            <ChartDetail data={chartDetail} />
          </div>
        )}
      </div>
      <Tabs style={{ marginTop: 40 }} defaultActiveKey="revenue" items={[
        {
          key: 'revenue',
          label: <span style={{ fontSize: 16, fontWeight: 700 }}>Tổng quan</span>,
          children: (<>
            {packagesDay.length > 0 && (<>
              <h2>Chi tiết theo ngày</h2>
              <TableDetailPackageByDay
                data={packagesDay}
                day={search.day}
                network={search.network}
              />
            </>)}
            <h2>Chi tiết luỹ kế</h2>
            <TableDetailPackageByMonth
              data={packages}
              renderButtonDetailPackage={renderButtonDetailPackage}
              network={search.network}
            />
          </>),
        },
        {
          key: 'vtvcab',
          label: <span style={{ fontSize: 16, fontWeight: 700 }}>VTVCab</span>,
          children: (<>
            {vtvcabDay.length > 0 && (<>
              <h2>Chi tiết theo ngày</h2>
              <TableDetailPackageByDay
                data={vtvcabDay}
                day={search.day}
                network={search.network}
              />
            </>)}
            <h2>Chi tiết luỹ kế</h2>
            <TableDetailPackageByMonth
              data={vtvcab}
              renderButtonDetailPackage={renderButtonDetailPackage}
              network={search.network}
            />
          </>),
        }
      ]} />
      <Modal
        className="custom-modal-width"
        title={<h3>{modalDetailPackage.packageName}</h3>}
        open={modalDetailPackage.isOpen}
        onOk={() => alert()}
        onCancel={() => setModalDetailPackage({ isOpen: false, data: null, packageName: '' })}
        footer={[]}>
        {modalDetailPackage.isOpen && (
          <div>
            <ChartComparisonDay data={modalDetailPackage.data} />
          </div>
        )}
      </Modal>
    </div>
  )
}
